import { useEffect, useState } from 'react';
import { ref, onValue, remove } from 'firebase/database'; // Added remove
import { getStorage } from 'firebase/storage';
import { AnimatePresence } from 'framer-motion';
import { SUBSCRIPTION_DETAILS } from './constants/subscriptionConfig';
import { useMusicUpload } from './hooks/useMusicUpload';
import { useArtistContent } from './hooks/useArtistContent';
import { useAudioPlayer } from '../admin/hooks/useAudioPlayer';
import { ArtistHeader } from './components/ArtistHeader';
import { UploadSection } from './components/UploadSection';
import { MusicUploadForm } from './components/MusicUploadForm';
import { VideoUploadForm } from './components/VideoUploadForm';
import { MusicTable } from './components/MusicTable';
import { VideoTable } from './components/VideoTable';
import { MusicPlayer } from '../admin/components/musicPlayer';

const storage = getStorage();

export default function ArtistDashboard({ db, currentUser, showToast }) {
  const [artistData, setArtistData] = useState(null);
  const [uploadingMusic, setUploadingMusic] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [showMusicForm, setShowMusicForm] = useState(false);
  const [showVideoForm, setShowVideoForm] = useState(false);

  const [musicForm, setMusicForm] = useState({
    name: '',
    artist: '',
    album: '',
    type: 'song',
    trackNumber: '',
    description: ''
  });

  const [videoForm, setVideoForm] = useState({
    name: '',
    youtubeUrl: '',
    description: ''
  });

  const musicUpload = useMusicUpload(storage, currentUser, showToast);
  const { uploadedMusic, uploadedVideos, loading } = useArtistContent(db, currentUser);
  const { isPlaying, currentTime, duration, currentTrack, playTrack, pauseTrack, seekTo, stopTrack, formatTime } = useAudioPlayer();

  // --- DELETE MUSIC LOGIC ---
  const handleDeleteMusic = async (musicKey) => {
    try {
      const trackToDelete = uploadedMusic.find(m => m.key === musicKey);
      if (!trackToDelete) return;

      // 1. Stop current track if it's being deleted
      if (currentTrack?.key === musicKey) stopTrack();

      // 2. Delete file from Firebase Storage
      // if (trackToDelete.storagePath) {
      //   // If the stored path is a gs:// URL, convert it to relative path
      //   try {
      //       const fileRef = storageRef(storage, filePath);
      //       await deleteObject(fileRef);
      //       console.log(`Cleaned up file from Storage: ${filePath}`);
      //     } catch (error) {
      //       console.error("Error cleaning up storage file:", error.message);
      //     }
      // }

      // 3. Delete metadata from Realtime Database
      const dbMusicRef = ref(db, `artistContent/music/${musicKey}`);
      await remove(dbMusicRef);

      showToast('success', 'Music deleted successfully');
    } catch (error) {
      console.error("Delete failed:", error);
      showToast('error', 'Failed to delete music');
    }
  };

  const handlePlayMusic = (url, music) => {
    if (currentTrack?.key === music.key && isPlaying) {
      pauseTrack();
    } else {
      playTrack(url, music);
    }
  };

  const handleTogglePlayPause = () => {
    if (isPlaying) {
      pauseTrack();
    } else if (currentTrack) {
      playTrack(currentTrack.url, currentTrack);
    }
  };

  useEffect(() => {
    if (!currentUser) return;
    const artistRef = ref(db, `artists/${currentUser.uid}`);
    const unsubscribe = onValue(artistRef, (snapshot) => {
      const data = snapshot.val();
      setArtistData(data);
    });
    return () => unsubscribe();
  }, [currentUser, db]);

  const canUploadMusic = () => {
    if (!artistData || artistData.status !== 'active' || !artistData.subscriptionTier) return false;
    const tier = SUBSCRIPTION_DETAILS[artistData.subscriptionTier];
    if (!tier) return false;
    if (tier.features[0].unlimited) return true;
    return (uploadedMusic.length || 0) < tier.features[0].limit;
  };

  const canUploadVideo = () => {
    if (!artistData || artistData.status !== 'active' || !artistData.subscriptionTier) return false;
    const tier = SUBSCRIPTION_DETAILS[artistData.subscriptionTier];
    if (!tier) return false;
    if (tier.features[1].unlimited) return true;
    return (uploadedVideos.length || 0) < tier.features[1].limit;
  };

  const handleMusicUploadSuccess = () => {
    setMusicForm({ name: '', artist: '', album: '', type: 'song', trackNumber: '', description: '' });
    setShowMusicForm(false);
    musicUpload.clearUploadState(); 
    setUploadingMusic(false);
  };

  const handleVideoUploadSuccess = () => {
    setVideoForm({ name: '', youtubeUrl: '', description: '' });
    setShowVideoForm(false);
    setUploadingVideo(false);
  };

  if (!artistData) {
    return <div className="text-center py-12 text-gray-500">Loading artist data...</div>;
  }

  const subscriptionTier = SUBSCRIPTION_DETAILS[artistData.subscriptionTier];

  return (
    <div className="space-y-8">
      <ArtistHeader artistData={artistData} subscriptionTier={subscriptionTier} />

      {artistData.status === 'active' && (
        <UploadSection
          artistData={{ ...artistData, uploadedMusic: uploadedMusic.length, uploadedVideos: uploadedVideos.length }}
          subscriptionTier={subscriptionTier}
          canUploadMusic={canUploadMusic}
          canUploadVideo={canUploadVideo}
          onMusicUploadClick={() => setShowMusicForm(true)}
          onVideoUploadClick={() => setShowVideoForm(true)}
        />
      )}

      <div className="space-y-4">
        <h3 className="text-xl font-bold text-gray-900">Music Uploads</h3>
        <MusicTable 
          music={uploadedMusic} 
          loading={loading} 
          onPlayMusic={handlePlayMusic}
          onDeleteMusic={handleDeleteMusic} // Passed function here
          currentTrack={currentTrack}
          isPlaying={isPlaying}
        />
      </div>

      <div className="space-y-4">
        <h3 className="text-xl font-bold text-gray-900">Video Uploads</h3>
        <VideoTable videos={uploadedVideos} loading={loading} />
      </div>

      <AnimatePresence>
        {showMusicForm && artistData.status === 'active' && (
          <MusicUploadForm
            isOpen={showMusicForm}
            onClose={() => setShowMusicForm(false)}
            musicForm={musicForm}
            setMusicForm={setMusicForm}
            fileUploaded={musicUpload.fileUploaded}
            uploadedFilePath={musicUpload.uploadedFilePath}
            uploadProgress={musicUpload.uploadProgress}
            uploadError={musicUpload.uploadError}
            fileInputRef={musicUpload.fileInputRef}
            isUploading={uploadingMusic}
            canUpload={canUploadMusic}
            db={db}
            currentUser={currentUser}
            onUploadSuccess={handleMusicUploadSuccess}
            onFileUpload={(file) => musicUpload.handleFileUpload(file, musicForm, setMusicForm)}
            showToast={showToast}
            resetUpload={musicUpload.resetUpload}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showVideoForm && artistData.status === 'active' && (
          <VideoUploadForm
            isOpen={showVideoForm}
            onClose={() => setShowVideoForm(false)}
            videoForm={videoForm}
            setVideoForm={setVideoForm}
            isUploading={uploadingVideo}
            canUpload={canUploadVideo}
            db={db}
            currentUser={currentUser}
            onUploadSuccess={handleVideoUploadSuccess}
            showToast={showToast}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {currentTrack && (
          <MusicPlayer
            track={currentTrack}
            isPlaying={isPlaying}
            currentTime={currentTime}
            duration={duration}
            onTogglePlay={handleTogglePlayPause}
            onSeek={seekTo}
            onClose={stopTrack}
            formatTime={formatTime}
          />
        )}
      </AnimatePresence>
    </div>
  );
}